import gsap from "gsap";
import { Flip } from "gsap/Flip";

gsap.registerPlugin(Flip);

export class ImageZoom {
  /**
   * @param {HTMLElement[]} elements
   */
  constructor(elements) {
    this.state = {
      elements,
      currentIndex: 0,
      originalThumbnail: null,
      prefersReducedMotion: window.matchMedia(
        "(prefers-reduced-motion: reduce)"
      ).matches,
      touchStartX: null,
      touchStartY: null,
      isTransitioning: false,
      isZooming: false,
    };

    this.dom = {
      container: document.querySelector(".zoomed-image"),
      closeButton: document.querySelector(".zoomed-image__close-button"),
      caption: document.querySelector(".zoomed-image__caption"),
      prevButton: document.querySelector(".zoomed-image__button--previous"),
      nextButton: document.querySelector(".zoomed-image__button--next"),
    };

    this.bindMethods();
    this.setupEventListeners();
  }

  get animationConfig() {
    return {
      duration: this.state.prefersReducedMotion ? 0 : 0.6,
      ease: this.state.prefersReducedMotion ? "none" : "power1.inOut",
    };
  }

  bindMethods() {
    this.handleKeydown = this.handleKeydown.bind(this);
    this.handlePrevNext = this.handlePrevNext.bind(this);
    this.close = this.close.bind(this);
  }

  setupEventListeners() {
    window
      .matchMedia("(prefers-reduced-motion: reduce)")
      .addEventListener("change", (e) => {
        this.state.prefersReducedMotion = e.matches;
      });

    if (this.dom.prevButton && this.dom.nextButton) {
      this.dom.prevButton.addEventListener("click", () =>
        this.handlePrevNext("prev")
      );
      this.dom.nextButton.addEventListener("click", () =>
        this.handlePrevNext("next")
      );
    }

    this.state.elements.forEach((button, index) => {
      button.addEventListener("click", () => {
        if (this.state.isZooming || this.state.isTransitioning) return;

        const image = button.querySelector("img");
        if (image) {
          this.state.currentIndex = index;
          this.zoom(image, button);
        }
      });
    });

    this.setupTouchControls();
  }

  setupTouchControls() {
    this.dom.container.addEventListener("touchstart", (e) => {
      this.state.touchStartX = e.touches[0].clientX;
      this.state.touchStartY = e.touches[0].clientY;
    });

    this.dom.container.addEventListener("touchend", (e) => {
      if (!this.state.touchStartX) return;

      const touchEndX = e.changedTouches[0].clientX;
      const touchEndY = e.changedTouches[0].clientY;
      const diffX = touchEndX - this.state.touchStartX;
      const diffY = Math.abs(touchEndY - this.state.touchStartY);
      const minSwipeDistance = 50;

      if (Math.abs(diffX) > minSwipeDistance && Math.abs(diffX) > diffY) {
        diffX > 0 ? this.handlePrevNext("prev") : this.handlePrevNext("next");
      }

      this.state.touchStartX = null;
      this.state.touchStartY = null;
    });
  }

  /**
   * @param {HTMLImageElement} thumbnail
   * @param {HTMLElement} button
   */
  createClone(thumbnail, button) {
    const clone = thumbnail.cloneNode(true);
    clone.classList = "zoomed-image__image";
    clone.removeAttribute("loading");
    gsap.set(clone, {
      opacity: 1,
    });
    clone.style.objectFit = "contain";

    if (button.dataset.zoom) {
      clone.src = button.dataset.zoom;
      clone.width = button.dataset.fullWidth;
      clone.height = button.dataset.fullHeight;
    } else {
      clone.src = thumbnail.src;
      clone.width = thumbnail.width;
      clone.height = thumbnail.height;
    }

    if (this.dom.caption) {
      this.dom.caption.innerHTML = button.dataset.caption || "";
    }

    return clone;
  }

  /**
   * @param {HTMLImageElement} thumbnail
   * @param {HTMLElement} button
   */
  zoom(thumbnail, button) {
    this.state.isZooming = true;
    this.toggleLoading(true);
    this.state.originalThumbnail = thumbnail;
    const clone = this.createClone(thumbnail, button);

    gsap.set(this.dom.container, { autoAlpha: 0 });
    this.dom.container.insertBefore(clone, this.dom.closeButton);

    const tempImage = new Image();

    tempImage.onload = () => {
      this.toggleLoading(false);
      gsap.to(this.dom.container, {
        autoAlpha: 1,
        ...this.animationConfig,
        onComplete: () => {
          document.activeElement.blur();
          this.dom.container.focus();
          this.state.isZooming = false;
        },
      });

      this.animateZoom(clone, thumbnail);
    };

    tempImage.onerror = () => {
      this.toggleLoading(false);
      this.state.isZooming = false;
    };

    tempImage.src = clone.src;
  }

  /**
   * @param {HTMLImageElement} clone
   * @param {HTMLImageElement} thumbnail
   */
  animateZoom(clone, thumbnail) {
    const state = Flip.getState(clone);
    Flip.fit(clone, thumbnail);

    Flip.to(state, {
      ...this.animationConfig,
      onComplete: () => {
        gsap.to(clone, {
          clearProps: "all",
          ...this.animationConfig,
        });
      },
    });

    this.setupCloseHandlers();
  }

  /**
   * @param {'prev' | 'next'} direction
   */
  handlePrevNext(direction) {
    if (this.state.isTransitioning) return;

    this.state.isTransitioning = true;
    this.toggleLoading(true);

    const { elements, currentIndex } = this.state;
    const nextIndex =
      direction === "next"
        ? (currentIndex + 1) % elements.length
        : (currentIndex - 1 + elements.length) % elements.length;

    const nextButton = elements[nextIndex];
    const nextImage = nextButton.querySelector("img");
    const currentImage = this.dom.container.querySelector(
      ".zoomed-image__image"
    );

    // Fade out current image
    gsap.to(currentImage, {
      autoAlpha: 0,
      duration: 0.25,
      ease: "power1.out",
      onComplete: () => {
        // Remove old image and prepare new one
        currentImage.remove();
        const newClone = this.createClone(nextImage, nextButton);
        gsap.set(newClone, { clearProps: "all" });
        gsap.set(newClone, { autoAlpha: 0 });
        this.dom.container.insertBefore(newClone, this.dom.closeButton);

        // Update state
        this.state.currentIndex = nextIndex;
        this.state.originalThumbnail = nextImage;

        // Show new image
        const fadeIn = () => {
          this.toggleLoading(false);
          gsap.to(newClone, {
            autoAlpha: 1,
            duration: 0.25,
            ease: "power1.inOut",
            onComplete: () => (this.state.isTransitioning = false),
          });
        };

        // Handle zoom image if present
        nextButton.dataset.zoom
          ? ((newClone.onload = fadeIn),
            (newClone.src = nextButton.dataset.zoom))
          : fadeIn();
      },
    });
  }

  /**
   * @param {KeyboardEvent} e
   */
  handleKeydown(e) {
    if (this.dom.container.style.visibility !== "hidden") {
      const keyActions = {
        Escape: this.close,
      };

      // Only add arrow key actions if we have navigation buttons
      if (this.dom.prevButton && this.dom.nextButton) {
        keyActions.ArrowRight = () => this.handlePrevNext("next");
        keyActions.ArrowLeft = () => this.handlePrevNext("prev");
      }

      keyActions[e.key]?.();
    }
  }

  setupCloseHandlers() {
    this.handleClose = (e) => {
      if (e.target.matches(".zoomed-image, .zoomed-image__image")) {
        this.close();
      }
    };

    this.dom.closeButton.addEventListener("click", this.close);
    this.dom.container.addEventListener("click", this.handleClose);
    window.addEventListener("keydown", this.handleKeydown);
  }

  close() {
    if (this.state.isTransitioning || this.state.isZooming) return;

    const clone = this.dom.container.querySelector(".zoomed-image__image");

    if (clone && this.state.originalThumbnail) {
      this.state.isZooming = true;
      this.removeEventListeners();

      const state = Flip.getState(clone);
      Flip.fit(clone, this.state.originalThumbnail);

      gsap.to(this.dom.container, {
        autoAlpha: 0,
        ...this.animationConfig,
      });

      Flip.from(state, {
        ...this.animationConfig,
        onComplete: () => {
          clone.remove();
          this.state.elements[this.state.currentIndex].focus();
          this.state.originalThumbnail = null;
          this.state.isZooming = false;
        },
      });
    }
  }

  /**
   * @param {boolean} isLoading
   */
  toggleLoading(isLoading) {
    document.documentElement.dataset.loading = isLoading;
  }

  removeEventListeners() {
    this.dom.closeButton.removeEventListener("click", this.close);
    this.dom.container.removeEventListener("click", this.handleClose);
    this.dom.container.removeEventListener("touchstart", this.handleTouchStart);
    this.dom.container.removeEventListener("touchend", this.handleTouchEnd);
    window.removeEventListener("keydown", this.handleKeydown);
  }
}
